import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock } from 'react-icons/fa';
import { contentAPI } from '../services/api';
import { useSiteInfo } from '../contexts/SiteInfoContext';
import { normalizePhoneNumbers, getAddressLines, getContactEmail } from '../utils/businessInfo';
import BookingForm from '../components/booking/BookingForm';
import HeroSection from '../components/layout/HeroSection';
import contactHeroImage from '../assets/images/office.jpg';
import './Contact.css';

const formatTime = (value) => {
  if (!value) {
    return '';
  }
  const [hourStr, minute] = value.split(':');
  let hour = parseInt(hourStr, 10);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${suffix}`;
};

const buildTradingHours = (hours) => {
  if (!hours) {
    return [];
  }
  return Object.entries(hours).map(([day, info]) => ({
    day: day.charAt(0).toUpperCase() + day.slice(1),
    hours: info.closed ? 'Closed' : `${formatTime(info.open)} - ${formatTime(info.close)}`,
  }));
};

const Contact = () => {
  const { siteInfo } = useSiteInfo();
  const businessInfo = siteInfo?.business_info ?? {};
  const [contactPage, setContactPage] = useState(null);
  const [disclaimerPage, setDisclaimerPage] = useState(null);
  const [businessStatus, setBusinessStatus] = useState({
    isOpen: false,
    label: 'Closed',
    message: 'Opens Monday 9:30 AM',
  });

  useEffect(() => {
    const loadPages = async () => {
      try {
        const response = await contentAPI.getPage('contact');
        setContactPage(response.data);
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.error('Failed to load contact page content', error);
        }
      }

      try {
        const response = await contentAPI.getPage('disclaimer');
        setDisclaimerPage(response.data);
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.error('Failed to load disclaimer content', error);
        }
      }
    };

    loadPages();
  }, []);

  useEffect(() => {
    const melbourneFormatter = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Melbourne',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });

    const timeDisplayFormatter = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Melbourne',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const timeZoneFormatter = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Melbourne',
      timeZoneName: 'short',
    });

    const openDays = [1, 2, 3, 4, 6]; // Mon-Thu & Sat
    const schedule = { open: 9.5, close: 17.5 };

    const getZoneAbbr = () => {
      const parts = timeZoneFormatter.formatToParts(new Date());
      return parts.find((part) => part.type === 'timeZoneName')?.value || 'AEST';
    };

    const formatTime = (decimalTime) => {
      const hours = Math.floor(decimalTime);
      const minutes = Math.round((decimalTime - hours) * 60);
      // Create a date in local time, not UTC, then format
      const reference = new Date(2020, 0, 1, hours, minutes);
      return timeDisplayFormatter.format(reference);
    };

    const computeStatus = () => {
      // Check for special closure first
      const specialClosure = businessInfo.special_closure;
      if (specialClosure) {
        setBusinessStatus({
          isOpen: false,
          label: 'Closed',
          message: specialClosure.message || 'Closed for special event',
        });
        return;
      }

      const parts = melbourneFormatter.formatToParts(new Date());
      const weekdayValue = parts.find((part) => part.type === 'weekday')?.value || 'Mon';
      const hourValue = Number(parts.find((part) => part.type === 'hour')?.value || 0);
      const minuteValue = Number(parts.find((part) => part.type === 'minute')?.value || 0);
      const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      const today = dayMap[weekdayValue.slice(0, 3)] ?? 1;
      const currentTimeDecimal = hourValue + minuteValue / 60;
      const isOperatingDay = openDays.includes(today);
      const zoneAbbr = getZoneAbbr();

      let isCurrentlyOpen = false;
      let message = '';

      if (isOperatingDay && currentTimeDecimal >= schedule.open && currentTimeDecimal < schedule.close) {
        isCurrentlyOpen = true;
        message = `Closes today at ${formatTime(schedule.close)} ${zoneAbbr}`;
      } else if (isOperatingDay && currentTimeDecimal < schedule.open) {
        message = `Opens today at ${formatTime(schedule.open)} ${zoneAbbr}`;
      } else {
        let offset = 1;
        let nextDay = (today + offset) % 7;
        while (!openDays.includes(nextDay) && offset <= 7) {
          offset += 1;
          nextDay = (today + offset) % 7;
        }

        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayLabel = dayNames[nextDay] || 'Monday';
        message = `Opens ${offset === 1 ? 'tomorrow' : `on ${dayLabel}`} at ${formatTime(schedule.open)} ${zoneAbbr}`;
      }

      setBusinessStatus({
        isOpen: isCurrentlyOpen,
        label: isCurrentlyOpen ? 'Open now' : 'Closed',
        message,
      });
    };

    computeStatus();
    const interval = setInterval(computeStatus, 60000);
    return () => clearInterval(interval);
  }, [businessInfo]);

  const tradingHours = useMemo(() => buildTradingHours(siteInfo?.hours), [siteInfo]);
  const company = siteInfo?.company ?? {};
  const phoneNumbers = useMemo(
    () => normalizePhoneNumbers(businessInfo, company),
    [businessInfo, company]
  );
  const addressLines = useMemo(
    () => getAddressLines(businessInfo, company),
    [businessInfo, company]
  );
  const contactEmail = useMemo(
    () => getContactEmail(businessInfo, company),
    [businessInfo, company]
  );
  const primaryPhoneHref = phoneNumbers[0]?.number ? `tel:${phoneNumbers[0].number.replace(/\s+/g, '')}` : null;
  const heroEyebrow =
    siteInfo?.site_settings?.['contact.hero_eyebrow'] || 'Talk to Automotive Care';
  const heroTitle = contactPage?.title || 'Contact Us';

  const heroLead =
    siteInfo?.site_settings?.['contact.hero_lead'] ||
    'Share your vehicle details, book an appointment, or ask a question. Our friendly team is ready to help Monday through Saturday.';

  const enquirySubtitle =
    siteInfo?.site_settings?.['contact.form_subtitle'] ||
    'Tell us about your vehicle and the service you require. We\'ll respond as soon as possible.';

  // Dynamic hero image with fallback to static (check for actual content, not just truthy value)
  const contactHeroImageSrc = (contactPage?.hero_image && contactPage.hero_image.trim()) || contactHeroImage;

  const disclaimerContent = disclaimerPage?.content;

  // Google Maps link for Automotive Care
  const googleMapsUrl = 'https://maps.app.goo.gl/g8M36g3PLF1ecsSo8';

  return (
    <>
      <Helmet>
        <title>Contact Us - Automotive Care</title>
        <meta
          name="description"
          content="Get in touch with Automotive Care in Laverton North. View trading hours, call our workshop, send an enquiry, or find our location on the map."
        />
      </Helmet>

      <HeroSection
        badge={heroEyebrow}
        title={heroTitle}
        subtitle={heroLead}
        backgroundImage={contactHeroImageSrc}
        showButtons={false}
      />

      <section className="section contact-quick-links">
        <div className="container">
          <div className="contact-hero-cards">
            {phoneNumbers.length > 0 && (
              <div className="hero-card">
                <FaPhone />
                <div>
                  <h4>Call the workshop</h4>
                  {phoneNumbers.map((phone, idx) => (
                    <a key={`${phone.number}-${idx}`} href={`tel:${phone.number.replace(/\s+/g, '')}`}>
                      {phone.number}
                    </a>
                  ))}
                </div>
              </div>
            )}
            <div className="hero-card">
              <FaEnvelope />
              <div>
                <h4>Email</h4>
                <a href={`mailto:${contactEmail || 'info@automotivecare.com.au'}`}>
                  {contactEmail || 'info@automotivecare.com.au'}
                </a>
              </div>
            </div>
            <div className="hero-card">
              <FaMapMarkerAlt />
              <div>
                <h4>Visit us</h4>
                <p>
                  {addressLines.length > 0
                    ? addressLines[0]
                    : '2A Supertron Court, Laverton North, VIC 3026'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section contact-section">
        <div className="container">
          <div className="contact-main-grid">
            <div className="contact-details-card">
              <h2>Get in touch</h2>
              <p>
                {siteInfo?.site_settings?.['contact.intro'] ||
                  'Reach out to discuss your servicing needs, request a quote, or organise a callback from our technicians.'}
              </p>

              <div className="contact-details">
                <div className="contact-status">
                  <span className={`status-pill ${businessStatus.isOpen ? 'open' : 'closed'}`}>
                    <span className="status-dot" />
                    {businessStatus.label}
                  </span>
                  <span className="status-note">{businessStatus.message}</span>
                </div>
                <div className="contact-detail">
                  <FaMapMarkerAlt />
                  <div>
                    <h3>Workshop Location</h3>
                    <p>
                      {addressLines.length > 0 ? (
                        addressLines.map((line, index) => (
                          <React.Fragment key={`${line}-${index}`}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))
                      ) : (
                        '2A Supertron Court, Laverton North, VIC 3026'
                      )}
                    </p>
                  </div>
                </div>

                <div className="contact-detail">
                  <FaPhone />
                  <div>
                    <h3>Call Us</h3>
                    <p>
                      {phoneNumbers.length > 0 ? (
                        phoneNumbers.map((phone, index) => (
                          <React.Fragment key={`${phone.number}-${index}`}>
                            <a href={`tel:${phone.number.replace(/\s+/g, '')}`}>{phone.number}</a>
                            {index < phoneNumbers.length - 1 && <br />}
                          </React.Fragment>
                        ))
                      ) : (
                        <a href="tel:0383682249">(03) 8368 2249</a>
                      )}
                    </p>
                  </div>
                </div>

                <div className="contact-detail">
                  <FaEnvelope />
                  <div>
                    <h3>Email</h3>
                    <p>
                      <a href={`mailto:${contactEmail || 'info@automotivecare.com.au'}`}>
                        {contactEmail || 'info@automotivecare.com.au'}
                      </a>
                    </p>
                  </div>
                </div>

                <div className="contact-detail" id="hours">
                  <FaClock />
                  <div>
                    <h3>Trading Hours</h3>
                    <ul className="trading-hours">
                      {tradingHours.map((item) => (
                        <li key={item.day}>
                          <span>{item.day}</span>
                          <span>{item.hours}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {primaryPhoneHref && (
                <a className="contact-call-to-action" href={primaryPhoneHref}>
                  Tap to call us now
                </a>
              )}
            </div>

            <div className="contact-form-card">
              <h2>Online Booking / Enquiry</h2>
              <p>{enquirySubtitle}</p>
              <BookingForm />
            </div>
          </div>
        </div>
      </section>

      <section className="section map-section">
        <div className="container">
          <div className="map-card">
            <iframe
              title="Automotive Care Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3150.928582208326!2d144.78216271153084!3d-37.83855777185236!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad68b05da5852cf%3A0x1d5b36c9d52c9dd7!2sAutomotive%20Care!5e0!3m2!1sen!2sau!4v1764489058587!5m2!1sen!2sau"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="map-actions">
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline"
            >
              <FaMapMarkerAlt /> Open in Google Maps
            </a>
          </div>
        </div>
      </section>

      {(disclaimerContent || siteInfo?.site_settings?.['contact.disclaimer']) && (
        <section className="section disclaimer-section">
          <div className="container">
            <div className="disclaimer-card">
              <h2>Disclaimer</h2>
              {disclaimerContent ? (
                <div dangerouslySetInnerHTML={{ __html: disclaimerContent }} />
              ) : (
                <p>{siteInfo?.site_settings?.['contact.disclaimer']}</p>
              )}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default Contact;
